import { CalendarDaysIcon, ClockIcon, VideoCameraIcon } from '@heroicons/react/24/solid';
import React, { useCallback, useEffect, useState } from 'react';
import { resolveThemePreset, type EventDto } from '@odo/shared-ui';
import { useToast } from '../../providers/ToastProvider';
import { fetchEventJoinUrl } from '../../services/events';
import { useEventSignUp } from './useEventSignUp';
import { useCountdown } from './useCountdown';

interface EventBannerRsvpProps {
    readonly event: EventDto;
}

const SignedUpBadge: React.FC<EventBannerRsvpProps> = ({ event }) => {
    const theme = resolveThemePreset(event.themePreset);
    const { canJoin, displayText } = useCountdown(event.startsAt);
    const [isJoining, setIsJoining] = useState(false);
    const { showToast } = useToast();

    const handleJoin = useCallback(
        async (e: React.MouseEvent) => {
            e.stopPropagation();
            // Opened synchronously, in the click's user-activation window, so browsers don't popup-block it.
            const meetingWindow = window.open('', '_blank');
            if (!meetingWindow) {
                showToast(
                    "Couldn't open the meeting - please allow pop-ups and try again",
                    'error'
                );
                return;
            }
            meetingWindow.opener = null;

            setIsJoining(true);
            try {
                const { url } = await fetchEventJoinUrl(event.id);
                meetingWindow.location.href = url;
            } catch {
                // `zoomUrl` is never sent to the client - no fallback URL to open on failure.
                meetingWindow.close();
                showToast("Couldn't open the meeting link - please try again", 'error');
            } finally {
                setIsJoining(false);
            }
        },
        [event.id, showToast]
    );

    // `countdownToEvent: false` means no gated join window - always show the action.
    if (!event.countdownToEvent || canJoin) {
        return (
            <button
                onClick={handleJoin}
                disabled={isJoining}
                className={`${theme.accent} ${theme.accentText} inline-flex items-center gap-1.5 px-5 py-1.5 rounded-full text-sm font-semibold shadow-md hover:shadow-lg hover:scale-105 active:scale-95 transition-all duration-150 disabled:opacity-50`}
            >
                <VideoCameraIcon className="w-4 h-4" />
                {isJoining ? 'Opening...' : 'Join Now'}
            </button>
        );
    }

    return (
        <span className="inline-flex items-center gap-1.5 border border-gray-900/30 text-gray-900/70 px-4 py-1.5 rounded-full text-sm font-medium cursor-default">
            <ClockIcon className="w-4 h-4" />
            Join in {displayText}
        </span>
    );
};

// The banner's RSVP surface. Only mounted for meetings, so an announcement
// never runs the sign-up / countdown / join hooks.
export const EventBannerRsvp: React.FC<EventBannerRsvpProps> = ({ event }) => {
    const theme = resolveThemePreset(event.themePreset);
    const { isSignedUp, signUp, isLoading, error } = useEventSignUp();
    const { showToast } = useToast();

    useEffect(() => {
        if (error) {
            showToast(error, 'error');
        }
    }, [error, showToast]);

    if (isSignedUp(event.id)) {
        return <SignedUpBadge event={event} />;
    }

    const handleSignUp = (e: React.MouseEvent) => {
        e.stopPropagation();
        signUp(event.id);
    };

    return (
        <button
            onClick={handleSignUp}
            disabled={isLoading}
            className={`${theme.accent} ${theme.accentText} inline-flex items-center gap-1.5 px-5 py-1.5 rounded-full text-sm font-semibold shadow-md hover:shadow-lg hover:scale-105 active:scale-95 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed`}
        >
            <CalendarDaysIcon className="w-4 h-4" />
            {isLoading ? 'Signing Up...' : event.actionLabel}
        </button>
    );
};

export default EventBannerRsvp;
